import React from 'react';
import { Clock, Users, Heart } from 'lucide-react';

interface MealCardProps {
  id: string;
  name: string;
  description: string;
  prepTime: number;
  servings: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  tags: string[];
  imageUrl?: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  onSelect?: (mealId: string) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (mealId: string) => void;
}

const MealCard: React.FC<MealCardProps> = ({
  id,
  name,
  description,
  prepTime,
  servings,
  calories,
  protein,
  carbs,
  fat,
  tags,
  imageUrl,
  difficulty,
  onSelect,
  isFavorite = false,
  onToggleFavorite
}) => {
  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'Easy': return 'var(--color-success)';
      case 'Medium': return 'var(--color-warning)';
      case 'Hard': return 'var(--color-error)';
      default: return 'var(--color-text-muted)';
    }
  };

  const getTagColor = (tag: string) => {
    const tagColors: { [key: string]: string } = {
      'Low Carb': 'var(--color-primary)',
      'High Protein': 'var(--color-secondary)',
      'Anti-Inflammatory': 'var(--color-accent)',
      'Heart Healthy': 'var(--color-success)',
      'Bone Health': 'var(--color-sage-green)',
      'Energy Boost': 'var(--color-warning)',
      'Mood Support': 'var(--color-secondary)'
    };
    return tagColors[tag] || 'var(--color-text-muted)';
  };

  return (
    <div 
      className="bg-white rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-1"
      style={{ 
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}
      onClick={() => onSelect?.(id)}
    >
      <div className="relative">
        <div 
          className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
          style={{
            backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {!imageUrl && (
            <span className="text-4xl">🥗</span>
          )}
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite?.(id);
          }}
          className="absolute top-3 right-3 p-2 rounded-full bg-white shadow-md transition-all duration-200"
        >
          <Heart 
            className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`}
            style={{ color: isFavorite ? 'var(--color-error)' : 'var(--color-text-muted)' }}
          />
        </button>
        
        <div 
          className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium"
          style={{
            background: getDifficultyColor(difficulty),
            color: 'white'
          }}
        >
          {difficulty}
        </div>
      </div>
      
      <div className="p-4">
        <div className="mb-3">
          <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
            {name}
          </h3>
          <p className="text-sm line-clamp-2" style={{ color: 'var(--color-text-muted)' }}>
            {description}
          </p>
        </div>
        
        <div className="flex items-center justify-between mb-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          <div className="flex items-center space-x-1">
            <Clock className="h-4 w-4" />
            <span>{prepTime} min</span>
          </div>
          <div className="flex items-center space-x-1">
            <Users className="h-4 w-4" />
            <span>{servings} serving{servings > 1 ? 's' : ''}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(58, 175, 169, 0.1)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--color-primary)' }}>
              {calories}
            </span>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>cal</p>
          </div>
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(156, 106, 222, 0.1)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--color-secondary)' }}>
              {protein}g
            </span>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>protein</p>
          </div>
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(244, 182, 194, 0.1)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--color-accent)' }}>
              {carbs}g
            </span>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>carbs</p>
          </div>
          <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(163, 177, 138, 0.1)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--color-sage-green)' }}>
              {fat}g
            </span>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>fat</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 rounded-full text-xs font-medium"
              style={{
                background: `${getTagColor(tag)}20`,
                color: getTagColor(tag)
              }}
            >
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span
              className="px-2 py-1 rounded-full text-xs font-medium"
              style={{
                background: 'rgba(0,0,0,0.05)',
                color: 'var(--color-text-muted)'
              }}
            >
              +{tags.length - 3} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MealCard;
